import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, Truck, Store, Users, Calendar,
  MapPin, Send, AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { getCateringPackages, calculateCateringPrice, submitCateringRequest, getCateringEventTypes, getSiteContent, getSystemSettingValue } from '../lib/api';
import { getCmsUrlValue, getCmsValue } from '../lib/siteContent';
import SmokeFooter from '../components/SmokeFooter';
import SmokeNav from '../components/SmokeNav';
import { buildCateringFaq, buildFaqSchema, buildPublicStructuredData, PAGE_SEO, useJsonLd, useSEO } from '../lib/seo';
import { createCateringFunnelTracker, trackEvent } from '../lib/googleAnalytics';
import './SmokehouseSite.css';

/**
 * Publik cateringoffert-sida. Hämtar paket + live-pris från backend.
 * Route: /catering
 */

const A = '/assets/smokehouse/';

const PriorityImage = ({ fetchPriority, ...imageProps }) => (
  <img alt="" {...imageProps} fetchpriority={fetchPriority} />
);

const EU_ALLERGENER = [
  'Gluten', 'Skaldjur', 'Ägg', 'Fisk', 'Jordnötter', 'Sojabönor', 'Mjölk/laktos',
  'Nötter', 'Selleri', 'Senap', 'Sesamfrö', 'Svaveldioxid/sulfit', 'Lupin', 'Blötdjur',
];
const EVENT_TYPER = ['Kick-off', 'Sommarfest', 'Mingel', 'Familjefirande', 'Annat'];

const kr = (n) => `${Math.round(n).toLocaleString('sv-SE')} kr`;
const num = (v) => parseFloat(v) || 0;

const hasVassenCopy = (value) => /vassen|lindholmen/i.test(String(value || ''));

const cleanVassenCopy = (value) => String(value || '')
  .replace(/Äts på Vassen(?: Market)?,?\s*Lindholmen/gi, 'På plats hos oss')
  .replace(/hos oss på Vassen(?: Market)?(?:,?\s*Lindholmen)?/gi, 'hos oss')
  .replace(/komma till oss på Vassen(?: Market)? i Göteborg för att?\s*/gi, 'komma till oss för att ')
  .replace(/\s+på\s+Vassen(?: Market)?/gi, '')
  .replace(/Vassen(?: Market)?,?\s*Lindholmen/gi, '')
  .replace(/Vassen Market/gi, '')
  .replace(/\s{2,}/g, ' ')
  .replace(/\s+([,.])/g, '$1')
  .trim();

const renamePublicInclude = (item) => (
  /^cale\s+sall?ad$/i.test(String(item).trim()) ? 'Kale Salad' : item
);

const sanitizePublicCateringPackage = (pkg) => {
  const includes = Array.isArray(pkg?.includes)
    ? pkg.includes.map(renamePublicInclude)
    : pkg?.includes;
  const hasLocationCopy = ['slug', 'name', 'description', 'plats_text', 'plats_beskrivning']
    .some((field) => hasVassenCopy(pkg?.[field]));

  if (!hasLocationCopy) return { ...pkg, includes };

  return {
    ...pkg,
    name: cleanVassenCopy(pkg.name),
    description: cleanVassenCopy(pkg.description),
    plats_text: 'På plats hos oss',
    plats_beskrivning: 'Maten avnjuts på plats hos oss. Ingen leveransadress behövs.',
    includes,
  };
};

const DEFAULT_GUEST_COUNT = 30;
const PRICE_DEBOUNCE_MS = 400;
const ADDRESS_DEBOUNCE_MS = 1200;
const ADDRESS_MIN_LENGTH = 5;
const ISO_PAD_LENGTH = 2;
const SUNDAY_TO_MONDAY_OFFSET = 6;
const CATERING_SUBMIT_TIMEOUT_MS = 15_000;

export default function CateringOffert() {
  useSEO({ ...PAGE_SEO['/catering'], path: '/catering' });
  const [paket, setPaket] = useState([]);
  const [laddarPaket, setLaddarPaket] = useState(true);
  const [siteContent, setSiteContent] = useState([]);
  const [basAdress, setBasAdress] = useState('');

  const [antal, setAntal] = useState(DEFAULT_GUEST_COUNT);
  const [datum, setDatum] = useState('');
  const [adress, setAdress] = useState('');
  const [manuellMil, setManuellMil] = useState(''); // fallback om ORS ej kan slå upp
  const [visaManuell, setVisaManuell] = useState(false);
  const [valtSlug, setValtSlug] = useState('');

  const [allergiText, setAllergiText] = useState('');
  const [allergener, setAllergener] = useState([]);
  const [vegPortioner, setVegPortioner] = useState(0);

  const [namn, setNamn] = useState('');
  const [epost, setEpost] = useState('');
  const [telefon, setTelefon] = useState('');
  const [foretag, setForetag] = useState('');
  const [orgnr, setOrgnr] = useState('');
  const [eventTyp, setEventTyp] = useState('');
  const [eventTyper, setEventTyper] = useState(EVENT_TYPER);
  const [meddelande, setMeddelande] = useState('');

  const [pris, setPris] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcInfo, setCalcInfo] = useState(null); // {needs_address|allow_manual, message}
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [kvittens, setKvittens] = useState(null);
  const latestPriceInputRef = useRef('');
  const submitInFlightRef = useRef(false);
  const contactInteractedRef = useRef(false);
  const funnelRef = useRef(null);
  if (!funnelRef.current) funnelRef.current = createCateringFunnelTracker(trackEvent);

  useEffect(() => {
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    funnelRef.current.track('page_view', { step_id: 'catering_page' });
  }, []);

  // Hämta paket
  useEffect(() => {
    (async () => {
      try {
        const data = await getCateringPackages();
        const synligaPaket = (Array.isArray(data) ? data : []).map(sanitizePublicCateringPackage);
        setPaket(synligaPaket);
        if (synligaPaket.length) setValtSlug(synligaPaket[0].slug);
      } catch (e) {
        // tomt — visa felmeddelande nedan
      }
      setLaddarPaket(false);
    })();
    (async () => {
      try {
        const typer = await getCateringEventTypes();
        if (Array.isArray(typer) && typer.length) setEventTyper(typer);
      } catch (e) {
        // behåll default-listan
      }
    })();
    (async () => {
      try {
        const { data } = await getSiteContent();
        if (Array.isArray(data)) setSiteContent(data);
      } catch (e) {
        // behåll CMS-defaults
      }
    })();
    (async () => {
      try {
        const value = await getSystemSettingValue('catering_bas_adress');
        if (value) setBasAdress(value);
      } catch (e) {
        // Inställningen är frivillig; dölj hjälpsatsen om den saknas.
      }
    })();
  }, []);

  const valtPaket = paket.find((p) => p.slug === valtSlug) || null;
  const giltigtVal = valtPaket && antal >= (valtPaket.min_guests || 1);
  const cms = (field) => getCmsValue(siteContent, 'catering_page', field);
  const seoCms = (field) => getCmsValue(siteContent, 'catering_seo', field);
  const seoCmsUrl = (field) => getCmsUrlValue(siteContent, 'catering_seo', field);
  const structuredData = useMemo(
    () => buildPublicStructuredData(siteContent, '/catering'),
    [siteContent]
  );
  useJsonLd('public-route-structured-data', structuredData);

  // FAQ-schemat byggs ur samma CMS-fält som texten på sidan, så strukturerad
  // data alltid matchar det besökaren ser (krav för FAQ-resultat i Google).
  const faqEntries = useMemo(() => buildCateringFaq(siteContent), [siteContent]);
  const faqSchema = useMemo(() => buildFaqSchema(faqEntries), [faqEntries]);
  useJsonLd('catering-faq', faqSchema);
  latestPriceInputRef.current = `${valtSlug}|${antal}|${adress.trim()}|${manuellMil}`;

  // Live-prisberäkning (debouncad). Adress får längre debounce och min-längdkrav
  // så ORS inte bombarderas med ofullständiga adresser som triggar allow_manual för tidigt.
  const beraknaPris = useCallback(async () => {
    if (!giltigtVal) { setPris(null); setCalcInfo(null); setVisaManuell(false); return; }
    const reqDelivery = valtPaket.requires_delivery;
    if (reqDelivery) {
      const adr = adress.trim();
      // För kort adress → visa needs_address, dölj manuell input
      if (!adr && !manuellMil) {
        setPris(null); setVisaManuell(false); setCalcInfo({ needs_address: true }); return;
      }
      if (adr.length < ADDRESS_MIN_LENGTH && !manuellMil) {
        setPris(null); setVisaManuell(false); setCalcInfo({ needs_address: true }); return;
      }
    }
    const priceInputKey = latestPriceInputRef.current;
    setCalcLoading(true);
    setSubmitError('');
    try {
      const payload = { slug: valtSlug, num_guests: antal };
      if (reqDelivery) {
        if (adress.trim()) payload.address = adress.trim();
        if (manuellMil) payload.distance_mil = num(manuellMil);
      }
      const { data, error } = await calculateCateringPrice(payload);
      if (priceInputKey !== latestPriceInputRef.current) return;
      if (error || !data) { setCalcInfo({ message: 'Kunde inte beräkna pris just nu.' }); setPris(null); }
      else {
        setPris(data.price || null);
        if (data.allow_manual) { setVisaManuell(true); setCalcInfo({ allow_manual: true, message: data.message }); }
        else { setVisaManuell(false); setCalcInfo(data.needs_address ? { needs_address: true } : null); }
      }
    } catch (e) {
      if (priceInputKey !== latestPriceInputRef.current) return;
      setCalcInfo({ message: 'Kunde inte beräkna pris just nu.' });
      setPris(null);
    }
    if (priceInputKey === latestPriceInputRef.current) setCalcLoading(false);
  }, [giltigtVal, valtPaket, valtSlug, antal, adress, manuellMil]);

  useEffect(() => {
    // Använd längre debounce när adressen ändras (så användaren hinner skriva klart)
    const isAddressChange = valtPaket?.requires_delivery && adress.trim().length > 0 && !manuellMil;
    const delay = isAddressChange ? ADDRESS_DEBOUNCE_MS : PRICE_DEBOUNCE_MS;
    const t = setTimeout(beraknaPris, delay);
    return () => clearTimeout(t);
  }, [beraknaPris, valtPaket, manuellMil, adress]);

  useEffect(() => {
    if (!datum || antal < 1) return;
    funnelRef.current.track('event_details', { step_id: 'event_date' });
    if (!giltigtVal) return;
    funnelRef.current.track('package_selected', { step_id: 'package' });
    if (pris) {
      funnelRef.current.track('estimate_viewed', { step_id: 'estimate' });
      if (contactInteractedRef.current) {
        funnelRef.current.track('contact_started', { step_id: 'contact' });
      }
    }
  }, [antal, datum, giltigtVal, pris, valtSlug]);

  const handleAdressChange = (value) => {
    setAdress(value);
    setCalcLoading(false);
    setVisaManuell(false);
    setCalcInfo(null);
  };

  const toggleAllergen = (a) =>
    setAllergener((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const markContactStarted = () => {
    contactInteractedRef.current = true;
    funnelRef.current.track('contact_started', { step_id: 'contact' });
  };

  const kanSkicka = giltigtVal && namn && epost && telefon && datum &&
    (!valtPaket.requires_delivery || adress.trim() || manuellMil) && pris;

  const skicka = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CATERING_SUBMIT_TIMEOUT_MS);
    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = {
        slug: valtSlug, num_guests: antal, event_date: datum,
        contact_name: namn, email: epost, phone: telefon,
        company_name: foretag || null, org_nr: orgnr || null,
        event_type: eventTyp || null, message: meddelande || null,
        allergies_text: allergiText || null, allergens: allergener, veg_portions: vegPortioner,
      };
      if (valtPaket.requires_delivery) {
        if (adress.trim()) payload.address = adress.trim();
        if (manuellMil) payload.distance_mil = num(manuellMil);
      }
      const { data, error } = await submitCateringRequest(payload, controller.signal);
      if (error || !data?.ok) throw new Error(error?.message || 'Något gick fel.');
      funnelRef.current.track('lead_success', { step_id: 'success' });
      setKvittens({ paket: valtPaket, antal, datum, adress, pris: data.price });
    } catch (e) {
      setSubmitError(controller.signal.aborted ? 'Kunde inte skicka förfrågan.' : (e.message || 'Kunde inte skicka förfrågan.'));
      funnelRef.current.track('submission_error', { step_id: 'error', error_type: 'submission' });
    } finally {
      clearTimeout(timeoutId);
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  if (kvittens) {
    const { paket: kp, pris: kpris } = kvittens;
    return (
      <SmokeShell siteContent={siteContent}>
        <section className="smoke-page">
          <div className="smoke-wrap smoke-success-wrap">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="smoke-panel smoke-success-panel"
            >
              <CheckCircle className="smoke-success-icon" />
              <span className="smoke-kicker">Förfrågan mottagen</span>
              <h1>{cms('receipt_title')}</h1>
              <p className="smoke-lead">
                {cms('receipt_lead')}
              </p>
              <div className="smoke-receipt" aria-label="Sammanfattning av cateringförfrågan">
                <Rad label="Paket" v={kp.name} />
                <Rad label="Antal personer" v={kvittens.antal} />
                <Rad label="Datum" v={kvittens.datum} />
                {kp.requires_delivery
                  ? <Rad label="Leveransadress" v={kvittens.adress} />
                  : <Rad label="Plats" v={kp.plats_text || 'På plats'} />}
                {kpris && (
                  <div className="smoke-receipt-total">
                    <Rad label="Prisuppskattning (inkl moms)" v={kr(kpris.total_inc_moms)} stark />
                  </div>
                )}
              </div>
              <button onClick={() => { setKvittens(null); }} className="smoke-inline-back" type="button">
                <ArrowLeft className="smoke-inline-icon" /> Gör en ny förfrågan
              </button>
            </motion.div>
          </div>
        </section>
      </SmokeShell>
    );
  }

  return (
    <SmokeShell siteContent={siteContent}>
      <section className="smoke-section smoke-catering-hero">
        <picture className="smoke-catering-hero-media">
          <PriorityImage
            src={`${A}smoker-process.jpg`}
            srcSet={`${A}smoker-process.jpg 960w`}
            sizes="100vw"
            width="960"
            height="1280"
            alt=""
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        <div className="smoke-wrap smoke-catering-hero-content">
          <div className="smoke-hero-copy smoke-catering-copy">
            <div className="smoke-catering-mark" aria-label="Härryda BBQ catering">
              <span>{cms('mark_label')}</span>
              <strong>{cms('mark_title')}</strong>
            </div>
            <span className="smoke-kicker">{cms('kicker')}</span>
            <h1>{cms('title')}</h1>
            <p className="smoke-lead">
              {cms('lead')}
            </p>
          </div>
        </div>
      </section>

      <section className="smoke-page smoke-catering-page" id="catering-form">
        <div className="smoke-wrap">
          <div className="smoke-configurator-head">
            <span className="smoke-kicker">{cms('builder_kicker')}</span>
            <h2>{cms('builder_title')}</h2>
            <p>{cms('builder_lead')}</p>
          </div>
          <form
            className="smoke-catering-layout"
            aria-label="Cateringförfrågan"
            onFocusCapture={() => {
              funnelRef.current.track('form_start', { step_id: 'form' });
            }}
            onClickCapture={() => {
              funnelRef.current.track('form_start', { step_id: 'form' });
            }}
            onSubmit={(event) => {
              event.preventDefault();
              if (!event.currentTarget.checkValidity()) {
                event.currentTarget.reportValidity();
                return;
              }
              if (kanSkicka && !submitInFlightRef.current) {
                submitInFlightRef.current = true;
                funnelRef.current.track('valid_submit', { step_id: 'submit' });
                skicka();
              }
            }}
          >
            <div className="smoke-steps">
              <Sektion nr="1" titel={cms('event_section_title')} className="smoke-configurator-step">
                <div className="smoke-form-row">
                  <label className="smoke-field">
                    <span className="smoke-field-head"><Users className="smoke-inline-icon" />Antal personer</span>
                    <input type="number" min="1" value={antal}
                      onChange={(e) => setAntal(parseInt(e.target.value, 10) || 0)} />
                  </label>
                  <label className="smoke-field">
                    <span className="smoke-field-head"><Calendar className="smoke-inline-icon" />Önskat datum</span>
                    <DatumValjare value={datum} onChange={setDatum} />
                  </label>
                </div>
              </Sektion>

              <Sektion nr="2" titel={cms('package_section_title')} id="catering-alternativ">
                {laddarPaket ? (
                  <div className="smoke-inline-status" aria-busy="true"><Loader2 className="smoke-spin smoke-inline-icon" /> Laddar alternativ…</div>
                ) : paket.length === 0 ? (
                  <p>Inga cateringalternativ tillgängliga just nu.</p>
                ) : (
                  <div className="smoke-choice-grid smoke-package-grid">
                    {paket.map((p) => {
                      const otillatet = antal < (p.min_guests || 1);
                      const vald = p.slug === valtSlug;
                      const includes = Array.isArray(p.includes) ? p.includes : [];
                      return (
                        <button key={p.slug} type="button" disabled={otillatet}
                          onClick={() => {
                            setValtSlug(p.slug);
                          }}
                          className={`smoke-choice smoke-package-choice${vald ? ' selected' : ''}${otillatet ? ' disabled' : ''}`}
                        >
                          {p.image_url && (
                            <img
                              src={p.image_url}
                              alt={p.name}
                              className="smoke-package-image"
                            />
                          )}
                          <span className="smoke-package-type">
                            {p.requires_delivery ? <Truck className="smoke-inline-icon" /> : <Store className="smoke-inline-icon" />}
                            {p.requires_delivery ? 'Catering på plats' : 'På plats hos oss'}
                          </span>
                          <h4>{p.name}</h4>
                          <p className="smoke-package-description">{p.description}</p>
                          <strong>
                            {Math.round(num(p.price_per_person))} kr <span>/person ex moms</span>
                          </strong>
                          <small>
                            {p.requires_delivery ? 'Catering på plats hos er' : (p.plats_text || 'På plats')} · moms {Math.round(num(p.vat_rate) * 100)}%
                          </small>
                          {includes.length > 0 && (
                            <ul className="smoke-include-list" aria-label={`Ingår: ${includes.join(', ')}`}>
                              {includes.map((i) => (
                                <li key={i}><CheckCircle className="smoke-check-icon" />{i}</li>
                              ))}
                            </ul>
                          )}
                          {otillatet && (
                            <span className="smoke-min-guests">Från {p.min_guests} personer</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Sektion>

              {valtPaket && (
                <Sektion nr="3" titel={valtPaket.requires_delivery ? cms('delivery_section_title') : cms('location_section_title')}>
                  {valtPaket.requires_delivery ? (
                    <div className="smoke-stack">
                      <label className="smoke-field">
                        <span className="smoke-field-head"><MapPin className="smoke-inline-icon" />{cms('delivery_address_label')}</span>
                        <input type="text" value={adress} placeholder={cms('delivery_address_placeholder')}
                          onChange={(e) => handleAdressChange(e.target.value)} />
                      </label>
                      {basAdress && <p>{cms('delivery_help_prefix')} {basAdress}.</p>}
                      {visaManuell && (
                        <div className="smoke-note-panel">
                          <label className="smoke-field smoke-short-field">
                            <span>{cms('delivery_manual_distance_label')}</span>
                            <input type="number" min="0" step="0.5" value={manuellMil}
                              onChange={(e) => setManuellMil(e.target.value)} />
                          </label>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="smoke-location-card">
                      <Store className="smoke-card-icon" />
                      <div>
                        <strong>{valtPaket.plats_text || 'På plats'}</strong>
                        <p>{valtPaket.plats_beskrivning || 'Ingen leveransadress behövs.'}</p>
                      </div>
                    </div>
                  )}
                </Sektion>
              )}

              <Sektion nr="4" titel={cms('allergy_section_title')}>
                <label className="smoke-field">
                  <span>Beskriv allergier eller specialkost</span>
                  <textarea rows="3" value={allergiText} onChange={(e) => setAllergiText(e.target.value)}
                    placeholder="Beskriv allergier eller specialkost i fritext…" />
                </label>
                <div className="smoke-field smoke-allergen-field">
                  <span>Förekommande allergener (EU:s 14) — klicka för att markera</span>
                  <div className="smoke-allergens">
                    {EU_ALLERGENER.map((a) => {
                      const on = allergener.includes(a);
                      return (
                        <button
                          type="button"
                          key={a}
                          onClick={() => toggleAllergen(a)}
                          aria-pressed={on}
                          className={on ? 'active' : ''}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="smoke-field smoke-short-field">
                  <span>Antal vegetariska/veganska portioner</span>
                  <input type="number" min="0" value={vegPortioner}
                    onChange={(e) => setVegPortioner(parseInt(e.target.value, 10) || 0)} />
                </label>
              </Sektion>

              <Sektion
                nr="5"
                titel={cms('contact_section_title')}
                onFocusCapture={markContactStarted}
              >
                <div className="smoke-form-row">
                  <label className="smoke-field"><span>Namn *</span>
                    <input name="contact_name" required value={namn} onChange={(e) => { markContactStarted(); setNamn(e.target.value); }} autoComplete="name" /></label>
                  <label className="smoke-field"><span>E-post *</span>
                    <input name="email" required type="email" value={epost} onChange={(e) => { markContactStarted(); setEpost(e.target.value); }} autoComplete="email" /></label>
                  <label className="smoke-field"><span>Telefon *</span>
                    <input name="phone" required type="tel" value={telefon} onChange={(e) => { markContactStarted(); setTelefon(e.target.value); }} autoComplete="tel" /></label>
                  <label className="smoke-field"><span>Typ av event</span>
                    <select value={eventTyp} onChange={(e) => setEventTyp(e.target.value)}>
                      <option value="">Välj…</option>
                      {eventTyper.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></label>
                  <label className="smoke-field"><span>Företag (frivilligt)</span>
                    <input name="company_name" value={foretag} onChange={(e) => setForetag(e.target.value)} autoComplete="organization" /></label>
                  <label className="smoke-field"><span>Org.nr (frivilligt)</span>
                    <input name="org_nr" value={orgnr} onChange={(e) => setOrgnr(e.target.value)} /></label>
                </div>
                <label className="smoke-field">
                  <span>Övriga önskemål</span>
                  <textarea name="message" rows="3" value={meddelande} onChange={(e) => setMeddelande(e.target.value)} />
                </label>
              </Sektion>
            </div>

            <PrisAside
              antal={antal}
              calcInfo={calcInfo}
              calcLoading={calcLoading}
              giltigtVal={giltigtVal}
              kanSkicka={kanSkicka}
              pris={pris}
              skicka={skicka}
              submitError={submitError}
              submitting={submitting}
              priceSectionTitle={cms('price_section_title')}
              submitLabel={cms('submit_label')}
              deliveryNeedsAddressText={cms('delivery_price_needs_address')}
              valtPaket={valtPaket}
            />
            <MobilPrisBar
              calcLoading={calcLoading}
              kanSkicka={kanSkicka}
              pris={pris}
              skicka={skicka}
              submitLabel={cms('submit_label')}
              submitting={submitting}
              valtPaket={valtPaket}
            />
          </form>
        </div>
      </section>

      <CateringSeoContent cms={seoCms} cmsUrl={seoCmsUrl} faqEntries={faqEntries} />
    </SmokeShell>
  );
}

/** Sökordsbärande innehåll under offertformuläret. All text redigeras i CMS:et. */
function CateringSeoContent({ cms, cmsUrl, faqEntries }) {
  const audiences = [1, 2, 3]
    .map((nr) => ({
      kicker: cms(`audience_${nr}_kicker`),
      title: cms(`audience_${nr}_title`),
      body: cms(`audience_${nr}_body`),
    }))
    .filter(({ title, body }) => title && body);
  const areas = String(cms('areas_list') || '')
    .split(',')
    .map((area) => area.trim())
    .filter(Boolean);

  return (
    <>
      {(cms('intro_title') || audiences.length > 0) && (
        <section className="smoke-routes" aria-labelledby="catering-intro-heading">
          <div className="smoke-wrap">
            <span className="smoke-kicker">{cms('intro_kicker')}</span>
            <h2 id="catering-intro-heading">{cms('intro_title')}</h2>
            <p className="smoke-lead">{cms('intro_lead')}</p>
            {cms('intro_link_label') && cmsUrl('intro_link_url') && (
              <p><Link to={cmsUrl('intro_link_url')}>{cms('intro_link_label')}</Link></p>
            )}
            {audiences.length > 0 && (
              <div className="smoke-route-grid">
                {audiences.map((audience) => (
                  <article className="smoke-route" key={audience.title}>
                    <span className="smoke-kicker">{audience.kicker}</span>
                    <div>
                      <h3>{audience.title}</h3>
                      <p>{audience.body}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {(areas.length > 0 || cms('steps_title')) && (
        <section className="smoke-proof" aria-labelledby="catering-areas-heading">
          <div className="smoke-wrap smoke-split">
            <div>
              <span className="smoke-kicker">{cms('areas_kicker')}</span>
              <h2 id="catering-areas-heading">{cms('areas_title')}</h2>
              <p className="smoke-lead">{cms('areas_lead')}</p>
              {areas.length > 0 && (
                <div className="smoke-proof-list">
                  {areas.map((area) => <span key={area}>{area}</span>)}
                </div>
              )}
            </div>
            {cms('steps_title') && (
              <div className="smoke-panel">
                <span className="smoke-kicker">{cms('steps_kicker')}</span>
                <h3>{cms('steps_title')}</h3>
                <p>{cms('steps_body')}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {faqEntries.length > 0 && (
        <section className="smoke-section smoke-faq" aria-labelledby="catering-faq-heading">
          <div className="smoke-wrap">
            <span className="smoke-kicker">{cms('faq_kicker')}</span>
            <h2 id="catering-faq-heading">{cms('faq_title')}</h2>
            <div className="smoke-faq-list">
              {faqEntries.map(({ question, answer }) => (
                <details className="smoke-faq-item" key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function SmokeShell({ children, siteContent = [] }) {
  return (
    <div className="smokehouse">
      <SmokeNav siteContent={siteContent} />
      <main>{children}</main>
      <SmokeFooter siteContent={siteContent} />
    </div>
  );
}

function PrisAside({
  antal,
  calcInfo,
  calcLoading,
  giltigtVal,
  kanSkicka,
  pris,
  skicka,
  submitError,
  submitting,
  priceSectionTitle,
  submitLabel,
  deliveryNeedsAddressText,
  valtPaket,
}) {
  return (
    <aside className="smoke-estimate smoke-live-estimate smoke-estimate-dark">
      <span className="smoke-kicker">{priceSectionTitle}</span>
      <h3>{valtPaket?.name || 'Välj alternativ'}</h3>

      {!giltigtVal ? (
        <div className="smoke-estimate-message">
          <AlertTriangle className="smoke-inline-icon" />
          <span>{valtPaket ? `Detta alternativ kräver minst ${valtPaket.min_guests} personer.` : 'Välj ett alternativ för att se priset.'}</span>
        </div>
      ) : calcInfo?.needs_address ? (
        <div className="smoke-estimate-message">
          <MapPin className="smoke-inline-icon" />
          <span>{deliveryNeedsAddressText}</span>
        </div>
      ) : !pris ? (
        <div className="smoke-estimate-message">
          {calcLoading ? <><Loader2 className="smoke-spin smoke-inline-icon" /> <span>Beräknar…</span></> : <span>{calcInfo?.message || 'Fyll i uppgifterna i formuläret.'}</span>}
        </div>
      ) : (
        <>
          <PrisRad label={`Matkostnad (${antal} × ${Math.round(num(valtPaket.price_per_person))} kr)`} v={pris.matkostnad} />
          {valtPaket.requires_delivery && <PrisRad label="Framkörning" v={pris.framkorning} />}
          <PrisRad label="Delsumma ex moms" v={pris.delsumma_ex_moms} />
          <PrisRad label={`Moms (mat ${Math.round(num(valtPaket.vat_rate) * 100)}%${valtPaket.requires_delivery ? ' + tjänst 25%' : ''})`} v={pris.moms} />
          <div className="smoke-estimate-total"><span>Totalt inkl moms</span><strong>{kr(pris.total_inc_moms)}</strong></div>
          <div><span>Totalt ex moms</span><span>{kr(pris.delsumma_ex_moms)}</span></div>
          {calcInfo?.allow_manual && (
            <p className="smoke-estimate-note">Avstånd angivet manuellt — slutpris bekräftas av oss.</p>
          )}
        </>
      )}

      {submitError && (
        <p className="smoke-form-status error" role="alert">{submitError}</p>
      )}

      <button type="submit" disabled={!kanSkicka || submitting} className="smoke-btn">
        {submitting ? <><Loader2 className="smoke-spin smoke-inline-icon" /> Skickar…</> : <><Send className="smoke-inline-icon" /> {submitLabel}</>}
      </button>
      <p>Detta är en offertförfrågan — inte en bindande bokning.</p>
    </aside>
  );
}

// Fast prisrad i botten på mobil — priset i PrisAside hamnar annars under
// alla formulärsteg och syns inte medan man fyller i. Visas ≤900px via CSS.
function MobilPrisBar({ calcLoading, kanSkicka, pris, submitLabel, submitting, valtPaket }) {
  if (!valtPaket) return null;

  return (
    <div className="smoke-price-bar">
      <div className="smoke-price-bar-info">
        <span>{valtPaket.name}</span>
        {pris ? (
          <strong>{kr(pris.total_inc_moms)} inkl moms</strong>
        ) : (
          <strong>{calcLoading ? 'Beräknar…' : 'Fyll i uppgifterna'}</strong>
        )}
      </div>
      <button
        type="submit"
        className="smoke-btn"
        disabled={!kanSkicka || submitting}

      >
        {submitting ? 'Skickar…' : submitLabel}
      </button>
    </div>
  );
}

// ---- Smokehouse-temad datumväljare (kalender-popover) ----
const MANADER = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const VECKODAGAR = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const toIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(ISO_PAD_LENGTH, '0')}-${String(d.getDate()).padStart(ISO_PAD_LENGTH, '0')}`;

function DatumValjare({ value, onChange }) {
  const [oppen, setOppen] = useState(false);
  const [visadManad, setVisadManad] = useState(() => (value ? new Date(value) : new Date()));
  const ref = useRef(null);

  useEffect(() => {
    const utanfor = (e) => { if (ref.current && !ref.current.contains(e.target)) setOppen(false); };
    document.addEventListener('mousedown', utanfor);
    return () => document.removeEventListener('mousedown', utanfor);
  }, []);

  const idag = new Date(); idag.setHours(0, 0, 0, 0);

  const dagar = useMemo(() => {
    const forsta = new Date(visadManad.getFullYear(), visadManad.getMonth(), 1);
    const start = new Date(forsta);
    const offset = forsta.getDay() === 0 ? SUNDAY_TO_MONDAY_OFFSET : forsta.getDay() - 1;
    start.setDate(forsta.getDate() - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  }, [visadManad]);

  const byt = (riktning) => setVisadManad((m) => new Date(m.getFullYear(), m.getMonth() + riktning, 1));
  const visat = value
    ? new Date(value).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Välj datum';

  return (
    <div className="smoke-date-picker" ref={ref}>
      <button type="button" onClick={() => setOppen((o) => !o)} className="smoke-date-trigger">
        <span className={value ? '' : 'placeholder'}>{visat}</span>
        <Calendar className="smoke-inline-icon" />
      </button>
      <AnimatePresence>
        {oppen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }} transition={{ duration: 0.15 }}
            className="smoke-date-popover">
            <div className="smoke-date-topbar">
              <button type="button" onClick={() => byt(-1)} aria-label="Föregående månad"><ChevronLeft className="smoke-inline-icon" /></button>
              <span>{MANADER[visadManad.getMonth()]} {visadManad.getFullYear()}</span>
              <button type="button" onClick={() => byt(1)} aria-label="Nästa månad"><ChevronRight className="smoke-inline-icon" /></button>
            </div>
            <div className="smoke-date-weekdays">
              {VECKODAGAR.map((d) => <div key={d}>{d}</div>)}
            </div>
            <div className="smoke-date-grid">
              {dagar.map((d, i) => {
                const iManad = d.getMonth() === visadManad.getMonth();
                const forflutet = d < idag;
                const vald = value && toIso(d) === value;
                const arIdag = toIso(d) === toIso(idag);
                return (
                  <button key={i} type="button" disabled={forflutet}
                    onClick={() => { onChange(toIso(d)); setOppen(false); }}
                    className={`${iManad ? '' : 'muted'}${vald ? ' selected' : ''}${arIdag ? ' today' : ''}`}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Sektion({ nr, titel, children, id, className = '', onFocusCapture }) {
  return (
    <motion.section
      id={id}
      className={`smoke-step${className ? ` ${className}` : ''}`}
      initial={false}
      onFocusCapture={onFocusCapture}
    >
      <div className="smoke-step-title">
        <span>{nr}</span>
        <h3>{titel}</h3>
      </div>
      {children}
    </motion.section>
  );
}

function PrisRad({ label, v }) {
  return <div><span>{label}</span><span>{kr(v)}</span></div>;
}
function Rad({ label, v, stark }) {
  return (
    <div className="smoke-receipt-row">
      <span>{label}</span>
      <strong className={stark ? 'strong' : ''}>{v}</strong>
    </div>
  );
}
